import { Link, useForm, usePage } from "@inertiajs/react"
import { type ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { SharedProps } from "@/types"

interface EventFormData {
  name: string
  description: string
  starts_at: string
  ends_at: string
  timezone: string
}

interface Props {
  region: { id: number; name: string }
  event: {
    id?: number
    name: string
    description: string
    starts_at: string | null
    ends_at: string | null
    timezone: string
  }
  urls: {
    submit: string
    method: "post" | "patch"
    region: string
    index: string
  }
}

const TIMEZONES = [
  { value: "UTC", label: "UTC" },
  { value: "Europe/Lisbon", label: "Lisbon (WET)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Europe/Berlin", label: "Berlin (CET)" },
  { value: "America/New_York", label: "New York (ET)" },
  { value: "America/Chicago", label: "Chicago (CT)" },
  { value: "America/Denver", label: "Denver (MT)" },
  { value: "America/Los_Angeles", label: "Los Angeles (PT)" },
  { value: "America/Sao_Paulo", label: "Sao Paulo (BRT)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Australia/Sydney", label: "Sydney (AEST)" },
]

export default function RegionEventForm({ region, event, urls }: Props) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, post, patch, processing, transform } = useForm<EventFormData>({
    name:        event.name,
    description: event.description,
    starts_at:   event.starts_at ?? "",
    ends_at:     event.ends_at ?? "",
    timezone:    event.timezone,
  })

  transform((d) => ({ event: d }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (urls.method === "patch") {
      patch(urls.submit)
    } else {
      post(urls.submit)
    }
  }

  const isEdit = !!event.id
  const title = isEdit ? `Edit event` : "Add event"

  return (
    <div>
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
          <Link href={urls.region} className="hover:underline">{region.name}</Link>
          <span aria-hidden="true">/</span>
          <Link href={urls.index} className="hover:underline">Events</Link>
          <span aria-hidden="true">/</span>
          <span>{title}</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Public event for {region.name}. Visible to all guests in the region.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="grid max-w-lg gap-5">
        <div className="grid gap-1.5">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={data.name}
            onChange={(e) => setData("name", e.target.value)}
            placeholder="Fado Night at Tasca do Chico"
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={data.description}
            onChange={(e) => setData("description", e.target.value)}
            rows={3}
            placeholder="A short description guests will see in the guide."
          />
          {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="starts_at">Starts</Label>
            <Input
              id="starts_at"
              type="datetime-local"
              value={data.starts_at}
              onChange={(e) => setData("starts_at", e.target.value)}
            />
            {errors.starts_at && <p className="text-xs text-destructive">{errors.starts_at}</p>}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="ends_at">Ends</Label>
            <Input
              id="ends_at"
              type="datetime-local"
              value={data.ends_at}
              onChange={(e) => setData("ends_at", e.target.value)}
            />
            {errors.ends_at && <p className="text-xs text-destructive">{errors.ends_at}</p>}
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="timezone">Timezone</Label>
          <Select value={data.timezone} onValueChange={(v) => setData("timezone", v)}>
            <SelectTrigger id="timezone">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.timezone && <p className="text-xs text-destructive">{errors.timezone}</p>}
        </div>

        {/* Visibility is always public for regional events — no UI control needed */}
        <input type="hidden" name="event[visibility]" value="public" />

        {errors.base && <p className="text-xs text-destructive">{errors.base}</p>}

        <div className="flex gap-3">
          <Button type="submit" size="sm" disabled={processing}>
            {processing ? "Saving…" : isEdit ? "Save changes" : "Add event"}
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link href={urls.index}>Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  )
}

RegionEventForm.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
